import { useRef } from "react";
import {
  CalendarPlus,
  CaretRight,
  CaretLeft,
  Check,
  Trash,
  CalendarX,
} from "@phosphor-icons/react";
import ModalCloseButton from "../../ui/ModalCloseButton";
import ConfirmDialog from "../../ui/ConfirmDialog";
import useAppointmentForm, { WIZARD_STEPS } from "./useAppointmentForm";
import WizardMiniSummary from "./WizardMiniSummary";
import PatientStep from "./steps/PatientStep";
import ScheduleStep from "./steps/ScheduleStep";
import DetailsStep from "./steps/DetailsStep";
import SummaryStep from "./steps/SummaryStep";
import BlockTimeStep from "./steps/BlockTimeStep";
import EditDialogs from "./edit/EditDialogs";
// Grow payment link chip / request button for an existing appointment (grow_integration.md Phase 5.3)
import GrowAppointmentPayment from "../../grow/GrowAppointmentPayment";
import "./AppointmentWizardModal.css";

const STEP_LABELS = ["מטופל", "מועד", "פרטים", "סיכום"];
// Blocked time is a two-step flow: type selection → the block screen.
const BLOCK_STEP_LABELS = ["סוג", "חסימה"];

/**
 * Multi-step appointment creation wizard (create-only — edit stays on the
 * classic AppointmentModal until Phase 2). Same prop contract as
 * AppointmentModal, routed through AppointmentEntryModal.
 */
function AppointmentWizardModal(props) {
  const form = useAppointmentForm(props);
  const {
    formData,
    step,
    maxStepReached,
    goToStep,
    nextStep,
    prevStep,
    returnToSummary,
    scheduleGateBlocked,
    detailsGateBlocked,
    submit,
    isSubmitting,
    handleSafeClose,
    showCloseWarning,
    setShowCloseWarning,
    onClose,
    treatmentTypes,
    accessibleRooms,
    canAccessFinance,
    PlanLimitModalComponent,
    isEditing,
    liveAppointment,
    isFormDirty,
    handleDeleteClick,
    startCancel,
    appointment,
    isBlockMode,
    submitBlock,
  } = form;

  const stepLabels = isBlockMode ? BLOCK_STEP_LABELS : STEP_LABELS;

  const nextBlocked =
    (step === WIZARD_STEPS.SCHEDULE && scheduleGateBlocked) ||
    (step === WIZARD_STEPS.DETAILS && detailsGateBlocked);

  const isCancelled = liveAppointment?.status === "cancelled";
  const isSoftDeleted = liveAppointment?.isDeleted === true;
  const showEditActions = isEditing && !isCancelled && !isSoftDeleted;

  // Close on overlay click only when the press STARTED on the overlay too.
  // Without this, closing an open dropdown mid-click shrinks the modal, the
  // mouseup lands on the overlay, and the whole wizard closes by accident.
  const overlayPressRef = useRef(false);

  return (
    <div
      className="modal-overlay apt-wiz-overlay"
      onMouseDown={(e) => {
        overlayPressRef.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (overlayPressRef.current && e.target === e.currentTarget) {
          handleSafeClose();
        }
        overlayPressRef.current = false;
      }}
    >
      <div className="apt-wiz-modal" onClick={(e) => e.stopPropagation()}>
        <div className="apt-wiz-header">
          <div className="apt-wiz-title">
            <div className="apt-wiz-title-icon">
              <CalendarPlus size={20} weight="fill" />
            </div>
            <div className="apt-wiz-title-text">
              <span className="apt-wiz-eyebrow">יומן פגישות</span>
              <h3>
                {isBlockMode
                  ? isEditing
                    ? "עריכת חסימת זמן"
                    : "חסימת זמן פרטי"
                  : isEditing
                  ? "עריכת פגישה"
                  : "קביעת פגישה חדשה"}
              </h3>
            </div>
          </div>
          <ModalCloseButton onClick={handleSafeClose} />
        </div>

        {!(isBlockMode && isEditing) && (
        <div className="apt-wiz-step-indicator">
          {stepLabels.map((label, idx) => {
            const num = idx + 1;
            const state =
              num === step ? "current" : num < step || (num <= maxStepReached && num !== step) ? "done" : "";
            const clickable = num <= maxStepReached && num !== step;
            return (
              <div key={label} className={`apt-wiz-step ${state}`}>
                {idx > 0 && (
                  <span
                    className={`apt-wiz-step-line ${num <= maxStepReached ? "filled" : ""}`}
                  />
                )}
                <button
                  type="button"
                  className="apt-wiz-step-dot"
                  disabled={!clickable}
                  onClick={() =>
                    clickable &&
                    goToStep(num, { fromSummary: step === WIZARD_STEPS.SUMMARY })
                  }
                  aria-label={`שלב ${num}: ${label}`}
                >
                  {state === "done" ? <Check size={11} weight="bold" /> : num}
                </button>
                <span className="apt-wiz-step-label">{label}</span>
              </div>
            );
          })}
        </div>
        )}

        <div className="apt-wiz-body">
          {/* Summary step already shows everything — chips are redundant there.
              Block mode has its own single screen, no mini summary. */}
          {step !== WIZARD_STEPS.SUMMARY && !isBlockMode && (
            <WizardMiniSummary
              formData={formData}
              step={step}
              maxStepReached={maxStepReached}
              treatmentTypes={treatmentTypes}
              accessibleRooms={accessibleRooms}
              canAccessFinance={canAccessFinance}
              goToStep={goToStep}
            />
          )}

          {step === WIZARD_STEPS.PATIENT && <PatientStep form={form} />}
          {step === WIZARD_STEPS.SCHEDULE &&
            (isBlockMode ? <BlockTimeStep form={form} /> : <ScheduleStep form={form} />)}
          {step === WIZARD_STEPS.DETAILS && <DetailsStep form={form} />}
          {step === WIZARD_STEPS.SUMMARY && <SummaryStep form={form} />}
        </div>

        <div className="apt-wiz-footer">
          {step === WIZARD_STEPS.SUMMARY && showEditActions && (
            <div className="apt-wiz-footer-danger">
              {canAccessFinance && !isBlockMode && (
                <div className="apt-wiz-footer-grow">
                  <GrowAppointmentPayment appointment={liveAppointment || appointment} />
                </div>
              )}
              <button
                type="button"
                className="apt-wiz-btn ghost-danger"
                onClick={handleDeleteClick}
                disabled={isSubmitting}
                title="מחיקת הפגישה"
              >
                <Trash size={15} />
                מחק
              </button>
              <button
                type="button"
                className="apt-wiz-btn ghost-warning"
                onClick={() => startCancel(appointment)}
                disabled={isSubmitting}
                title="ביטול הפגישה"
              >
                <CalendarX size={15} />
                בטל פגישה
              </button>
            </div>
          )}
          {/* Block edit: delete only — a blocked slot is never "cancelled" */}
          {isBlockMode && isEditing && !isSoftDeleted && (
            <div className="apt-wiz-footer-danger">
              <button
                type="button"
                className="apt-wiz-btn ghost-danger"
                onClick={handleDeleteClick}
                disabled={isSubmitting}
                title="מחיקת החסימה"
              >
                <Trash size={15} />
                מחק
              </button>
            </div>
          )}
          {step > WIZARD_STEPS.PATIENT && !(isEditing && (step === WIZARD_STEPS.SUMMARY || isBlockMode)) && (
            <button
              type="button"
              className="apt-wiz-btn secondary"
              onClick={prevStep}
              disabled={isSubmitting}
            >
              <CaretRight size={16} />
              חזרה
            </button>
          )}
          {isBlockMode && step === WIZARD_STEPS.SCHEDULE ? (
            <button
              type="button"
              className="apt-wiz-btn primary"
              onClick={submitBlock}
              disabled={isSubmitting || (isEditing && !isFormDirty)}
            >
              <Check size={16} weight="bold" />
              {isSubmitting
                ? isEditing
                  ? "מעדכן..."
                  : "חוסם..."
                : isEditing
                ? "עדכון חסימה"
                : "חסימת זמן"}
            </button>
          ) : step < WIZARD_STEPS.SUMMARY ? (
            <button
              type="button"
              className="apt-wiz-btn primary"
              onClick={nextStep}
              disabled={nextBlocked}
            >
              {returnToSummary || isEditing ? "חזרה לסיכום" : "המשך"}
              <CaretLeft size={16} />
            </button>
          ) : (
            <button
              type="button"
              className="apt-wiz-btn primary"
              onClick={submit}
              disabled={isSubmitting || (isEditing && (!isFormDirty || isCancelled || isSoftDeleted))}
            >
              <Check size={16} weight="bold" />
              {isSubmitting
                ? isEditing
                  ? "מעדכן..."
                  : "יוצר פגישה..."
                : isEditing
                ? "עדכון פגישה"
                : formData.isRecurring
                ? "יצירת סדרה"
                : "קביעת פגישה"}
            </button>
          )}
        </div>
      </div>

      {/* stopPropagation: dialog clicks must not bubble to the wizard overlay */}
      {isEditing && (
        <div onClick={(e) => e.stopPropagation()}>
          <EditDialogs form={form} />
        </div>
      )}

      <ConfirmDialog
        isOpen={showCloseWarning}
        onClose={() => setShowCloseWarning(false)}
        onConfirm={() => {
          setShowCloseWarning(false);
          onClose();
        }}
        title="לצאת בלי לשמור?"
        message="הפרטים שהוזנו לפגישה יימחקו."
        confirmText="יציאה"
        cancelText="המשך עריכה"
        type="warning"
      />

      <PlanLimitModalComponent />
    </div>
  );
}

export default AppointmentWizardModal;
